/**
 * RecipeForm Component
 *
 * Form for creating and editing recipes with dynamic ingredient management.
 * Handles validation, loading states, and error handling.
 */

import { useState, FormEvent, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useRecipeStore } from '../../stores/recipeStore';
import type { RecipeIngredientFormData } from '../../types/recipe';
import { RichTextEditor } from './RichTextEditor';
import './recipes.css';
import '../auth/auth.css';

interface RecipeFormProps {
  recipeId?: number;
  onSuccess?: () => void;
  onCancel?: () => void;
}

// ---------------------------------------------------------------------------
// Client-side nutrition calculation (mirrors NutritionCalculator in Rails)
// ---------------------------------------------------------------------------

const GRAM_UNITS  = ['г', 'гр', 'г.', 'g', 'gr', 'gram', 'grams', 'грамм', 'граммов'];
const ML_UNITS    = ['мл', 'ml', 'мл.', 'milliliter', 'milliliters'];
const PIECE_UNITS = ['шт', 'шт.', 'штука', 'штуки', 'штук', 'piece', 'pcs', 'pc'];

type UnitKind = 'weight' | 'volume' | 'piece' | 'unknown';

function classifyUnit(unit: string): UnitKind {
  const u = unit.trim().toLowerCase();
  if (GRAM_UNITS.includes(u))  return 'weight';
  if (ML_UNITS.includes(u))    return 'volume';
  if (PIECE_UNITS.includes(u)) return 'piece';
  return 'unknown';
}

interface NutritionTotals {
  calories: number;
  proteins: number;
  fats: number;
  carbohydrates: number;
  counted: number;
  skipped: number;
}

function calculateNutritionFromIngredients(
  ingredients: RecipeIngredientFormData[]
): NutritionTotals | null {
  let calories = 0, proteins = 0, fats = 0, carbohydrates = 0;
  let counted = 0, skipped = 0;

  for (const ing of ingredients) {
    const qty = Number(ing.quantity) || 0;
    if (qty <= 0 || !ing.unit) continue;

    const hasAnyNutrition =
      ing.ingredient_calories_per_100 != null ||
      ing.ingredient_proteins_per_100 != null ||
      ing.ingredient_fats_per_100 != null ||
      ing.ingredient_carbohydrates_per_100 != null;

    if (!hasAnyNutrition) continue;

    const kind = classifyUnit(ing.unit);
    let factor: number | null = null;

    if (kind === 'weight' || kind === 'volume') {
      factor = qty / 100;
    } else if (kind === 'piece') {
      const pw = Number(ing.ingredient_piece_weight_g) || 0;
      if (pw > 0) {
        factor = (qty * pw) / 100;
      }
    }

    if (factor === null) {
      skipped++;
      continue;
    }

    calories      += (ing.ingredient_calories_per_100      ?? 0) * factor;
    proteins      += (ing.ingredient_proteins_per_100      ?? 0) * factor;
    fats          += (ing.ingredient_fats_per_100          ?? 0) * factor;
    carbohydrates += (ing.ingredient_carbohydrates_per_100 ?? 0) * factor;
    counted++;
  }

  if (counted === 0) return null;

  return {
    calories:      Math.round(calories * 10) / 10,
    proteins:      Math.round(proteins * 10) / 10,
    fats:          Math.round(fats * 10) / 10,
    carbohydrates: Math.round(carbohydrates * 10) / 10,
    counted,
    skipped,
  };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function RecipeForm({ recipeId, onSuccess, onCancel }: RecipeFormProps) {
  const { t } = useTranslation(['recipes', 'common']);
  const navigate = useNavigate();
  const { currentRecipe, createRecipe, updateRecipe, fetchRecipe, isLoading, error, clearError } = useRecipeStore();

  // Form state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [instructions, setInstructions] = useState('');
  const [prepTime, setPrepTime] = useState<number | ''>('');
  const [cookTime, setCookTime] = useState<number | ''>('');
  const [servings, setServings] = useState<number | ''>(4);
  const [ingredients, setIngredients] = useState<RecipeIngredientFormData[]>([
    { ingredient_name: '', quantity: 0, unit: '' }
  ]);
  const [calories, setCalories] = useState<number | ''>('');
  const [proteins, setProteins] = useState<number | ''>('');
  const [fats, setFats] = useState<number | ''>('');
  const [carbohydrates, setCarbohydrates] = useState<number | ''>('');
  const [validationError, setValidationError] = useState<string | null>(null);

  // Track which ingredient rows have their nutrition panel expanded
  const [expandedNutrition, setExpandedNutrition] = useState<Set<number>>(new Set());

  // Live preview of nutrition calculated from ingredients
  const [nutritionPreview, setNutritionPreview] = useState<NutritionTotals | null>(null);

  // Load recipe data for edit mode
  useEffect(() => {
    if (recipeId) {
      fetchRecipe(recipeId);
    }
  }, [recipeId, fetchRecipe]);

  // Populate form when editing
  useEffect(() => {
    if (currentRecipe && recipeId === currentRecipe.id) {
      setTitle(currentRecipe.title);
      setDescription(currentRecipe.description || '');
      setInstructions(currentRecipe.instructions);
      setPrepTime(currentRecipe.prep_time || '');
      setCookTime(currentRecipe.cook_time || '');
      setServings(currentRecipe.servings);

      if (currentRecipe.recipe_ingredients && currentRecipe.recipe_ingredients.length > 0) {
        const mapped = currentRecipe.recipe_ingredients.map((ri) => ({
          id: ri.id,
          ingredient_id: ri.ingredient_id,
          ingredient_name: ri.ingredient.name,
          quantity: ri.quantity,
          unit: ri.unit,
          ingredient_calories_per_100:      ri.ingredient.calories_per_100      ?? undefined,
          ingredient_proteins_per_100:      ri.ingredient.proteins_per_100      ?? undefined,
          ingredient_fats_per_100:          ri.ingredient.fats_per_100          ?? undefined,
          ingredient_carbohydrates_per_100: ri.ingredient.carbohydrates_per_100 ?? undefined,
          ingredient_piece_weight_g:        ri.ingredient.piece_weight_g        ?? undefined,
        }));
        setIngredients(mapped);

        // Auto-expand nutrition rows that already have data
        const autoExpand = new Set<number>();
        mapped.forEach((ing, idx) => {
          if (
            ing.ingredient_calories_per_100 != null ||
            ing.ingredient_proteins_per_100 != null ||
            ing.ingredient_fats_per_100 != null ||
            ing.ingredient_carbohydrates_per_100 != null
          ) {
            autoExpand.add(idx);
          }
        });
        setExpandedNutrition(autoExpand);
      }

      setCalories(currentRecipe.calories ?? '');
      setProteins(currentRecipe.proteins ?? '');
      setFats(currentRecipe.fats ?? '');
      setCarbohydrates(currentRecipe.carbohydrates ?? '');
    }
  }, [currentRecipe, recipeId]);

  // Recalculate live preview whenever ingredients change
  useEffect(() => {
    setNutritionPreview(calculateNutritionFromIngredients(ingredients));
  }, [ingredients]);

  const handleAddIngredient = () => {
    setIngredients([...ingredients, { ingredient_name: '', quantity: 0, unit: '' }]);
  };

  const handleRemoveIngredient = (index: number) => {
    if (ingredients.length > 1) {
      setIngredients(ingredients.filter((_, i) => i !== index));
      setExpandedNutrition((prev) => {
        const next = new Set<number>();
        prev.forEach((i) => {
          if (i < index) next.add(i);
          else if (i > index) next.add(i - 1);
        });
        return next;
      });
    }
  };

  const handleIngredientChange = useCallback(
    (index: number, field: keyof RecipeIngredientFormData, value: string | number | null) => {
      setIngredients((prev) => {
        const next = [...prev];
        next[index] = { ...next[index], [field]: value };
        return next;
      });
    },
    []
  );

  const toggleNutritionPanel = (index: number) => {
    setExpandedNutrition((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  const handleApplyCalculated = () => {
    if (!nutritionPreview) return;
    setCalories(nutritionPreview.calories);
    setProteins(nutritionPreview.proteins);
    setFats(nutritionPreview.fats);
    setCarbohydrates(nutritionPreview.carbohydrates);
  };

  const validateForm = (): boolean => {
    if (!title.trim()) {
      setValidationError(t('titleRequired'));
      return false;
    }

    if (!instructions) {
      setValidationError(t('instructionsRequired'));
      return false;
    }

    if (servings && servings < 1) {
      setValidationError(t('servingsMin'));
      return false;
    }

    const validIngredients = ingredients.filter(
      (ing) => ing.ingredient_name?.trim() && ing.quantity > 0 && ing.unit.trim()
    );

    if (validIngredients.length === 0) {
      setValidationError(t('ingredientRequired'));
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    clearError();
    setValidationError(null);

    if (!validateForm()) return;

    const validIngredients = ingredients.filter(
      (ing) => ing.ingredient_name?.trim() && ing.quantity > 0 && ing.unit.trim()
    );

    const formData = {
      title: title.trim(),
      description: description.trim() || undefined,
      instructions: instructions,
      prep_time: prepTime || undefined,
      cook_time: cookTime || undefined,
      servings: Number(servings) || 1,
      calories:      calories      !== '' ? Number(calories)      : null,
      proteins:      proteins      !== '' ? Number(proteins)      : null,
      fats:          fats          !== '' ? Number(fats)          : null,
      carbohydrates: carbohydrates !== '' ? Number(carbohydrates) : null,
      recipe_ingredients_attributes: validIngredients,
    };

    try {
      if (recipeId) {
        await updateRecipe(recipeId, formData);
      } else {
        await createRecipe(formData);
      }

      if (onSuccess) {
        onSuccess();
      } else {
        navigate('/recipes');
      }
    } catch (err) {
      console.error('Failed to save recipe:', err);
    }
  };

  const displayError = validationError || error;

  const numericField = (value: number | null | undefined): number | '' => {
    if (value == null) return '';
    return value;
  };

  return (
    <div className="recipe-container">
      <div className="recipe-header">
        <h1>{recipeId ? t('editRecipe') : t('createNewRecipe')}</h1>
        <p className="recipe-subtitle">
          {recipeId ? t('updateSubtitle') : t('createSubtitle')}
        </p>
      </div>

      {displayError && (
        <div className="error-message" role="alert">
          {displayError}
        </div>
      )}

      <form onSubmit={handleSubmit} className="auth-form" style={{ maxWidth: '800px', margin: '0 auto', width: '100%' }}>
        {/* Basic Information */}
        <div className="form-group">
          <label htmlFor="title">{t('recipeTitleLabel')}</label>
          <input
            id="title"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
            placeholder={t('recipeTitlePlaceholder')}
            disabled={isLoading}
            maxLength={200}
          />
        </div>

        <div className="form-group">
          <label htmlFor="description">{t('descriptionLabel')}</label>
          <textarea
            id="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t('descriptionPlaceholder')}
            disabled={isLoading}
            rows={3}
            maxLength={500}
          />
          <small className="form-hint">{t('descriptionHint')}</small>
        </div>

        <div className="form-group">
          <label>{t('instructionsLabel')}</label>
          <RichTextEditor
            value={instructions}
            onChange={setInstructions}
            placeholder={t('instructionsPlaceholder')}
            disabled={isLoading}
          />
          <small className="form-hint">{t('instructionsHint')}</small>
        </div>

        {/* Time and Servings */}
        <div className="recipe-time-grid">
          <div className="form-group">
            <label htmlFor="prep-time">{t('prepTimeLabel')}</label>
            <input
              id="prep-time"
              type="number"
              value={prepTime}
              onChange={(e) => setPrepTime(e.target.value ? Number(e.target.value) : '')}
              placeholder="15"
              disabled={isLoading}
              min={0}
            />
          </div>

          <div className="form-group">
            <label htmlFor="cook-time">{t('cookTimeLabel')}</label>
            <input
              id="cook-time"
              type="number"
              value={cookTime}
              onChange={(e) => setCookTime(e.target.value ? Number(e.target.value) : '')}
              placeholder="30"
              disabled={isLoading}
              min={0}
            />
          </div>

          <div className="form-group">
            <label htmlFor="servings">{t('servingsRequired')}</label>
            <input
              id="servings"
              type="number"
              value={servings}
              onChange={(e) => setServings(e.target.value ? Number(e.target.value) : '')}
              required
              placeholder="4"
              disabled={isLoading}
              min={1}
            />
          </div>
        </div>

        {/* Ingredients Section */}
        <div className="form-group">
          <label>{t('ingredientsRequired')}</label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {ingredients.map((ingredient, index) => (
              <div key={index} style={{ border: '1px solid var(--border-color, #e2e8f0)', borderRadius: '8px', padding: '0.75rem' }}>
                {/* Main row: name / qty / unit / toggle / remove */}
                <div className="ingredient-row">
                  <div className="form-group" style={{ margin: 0 }}>
                    <input
                      type="text"
                      value={ingredient.ingredient_name || ''}
                      onChange={(e) => handleIngredientChange(index, 'ingredient_name', e.target.value)}
                      placeholder="Название (напр. мука)"
                      disabled={isLoading}
                      required
                    />
                  </div>

                  <div className="form-group" style={{ margin: 0 }}>
                    <input
                      type="number"
                      value={ingredient.quantity || ''}
                      onChange={(e) => handleIngredientChange(index, 'quantity', Number(e.target.value))}
                      placeholder="200"
                      disabled={isLoading}
                      required
                      min={0}
                      step="0.01"
                    />
                  </div>

                  <div className="form-group" style={{ margin: 0 }}>
                    <input
                      type="text"
                      value={ingredient.unit}
                      onChange={(e) => handleIngredientChange(index, 'unit', e.target.value)}
                      placeholder="г / мл / шт"
                      disabled={isLoading}
                      required
                    />
                  </div>

                  {/* Toggle КБЖУ panel */}
                  <button
                    type="button"
                    onClick={() => toggleNutritionPanel(index)}
                    disabled={isLoading}
                    className="btn-secondary"
                    style={{
                      padding: '0.5rem 0.6rem',
                      fontSize: '0.75rem',
                      minWidth: 'auto',
                      opacity: expandedNutrition.has(index) ? 1 : 0.6,
                    }}
                    aria-label="Указать КБЖУ ингредиента"
                    title="Указать КБЖУ ингредиента"
                  >
                    {ingredient.ingredient_calories_per_100 != null ? '🔥' : 'КБЖУ'}
                  </button>

                  <button
                    type="button"
                    onClick={() => handleRemoveIngredient(index)}
                    disabled={isLoading || ingredients.length === 1}
                    className="btn-secondary"
                    style={{ padding: '0.5rem 0.75rem', fontSize: '1.25rem', minWidth: 'auto' }}
                    aria-label="Remove ingredient"
                  >
                    ×
                  </button>
                </div>

                {/* Expandable КБЖУ panel */}
                {expandedNutrition.has(index) && (
                  <div
                    style={{
                      marginTop: '0.5rem',
                      paddingTop: '0.5rem',
                      borderTop: '1px dashed var(--border-color, #e2e8f0)',
                    }}
                  >
                    <small style={{ color: 'var(--text-secondary, #718096)', marginBottom: '0.4rem', display: 'block' }}>
                      КБЖУ на 100г / 100мл (или указать вес 1 штуки для единицы «шт»)
                    </small>
                    <div className="ingredient-kbzhu-grid">
                      <div className="form-group" style={{ margin: 0 }}>
                        <label style={{ fontSize: '0.7rem' }}>Ккал/100</label>
                        <input
                          type="number"
                          value={numericField(ingredient.ingredient_calories_per_100)}
                          onChange={(e) =>
                            handleIngredientChange(
                              index,
                              'ingredient_calories_per_100',
                              e.target.value !== '' ? Number(e.target.value) : null
                            )
                          }
                          placeholder="0"
                          min={0}
                          step="0.1"
                          disabled={isLoading}
                        />
                      </div>
                      <div className="form-group" style={{ margin: 0 }}>
                        <label style={{ fontSize: '0.7rem' }}>Б/100</label>
                        <input
                          type="number"
                          value={numericField(ingredient.ingredient_proteins_per_100)}
                          onChange={(e) =>
                            handleIngredientChange(
                              index,
                              'ingredient_proteins_per_100',
                              e.target.value !== '' ? Number(e.target.value) : null
                            )
                          }
                          placeholder="0"
                          min={0}
                          step="0.1"
                          disabled={isLoading}
                        />
                      </div>
                      <div className="form-group" style={{ margin: 0 }}>
                        <label style={{ fontSize: '0.7rem' }}>Ж/100</label>
                        <input
                          type="number"
                          value={numericField(ingredient.ingredient_fats_per_100)}
                          onChange={(e) =>
                            handleIngredientChange(
                              index,
                              'ingredient_fats_per_100',
                              e.target.value !== '' ? Number(e.target.value) : null
                            )
                          }
                          placeholder="0"
                          min={0}
                          step="0.1"
                          disabled={isLoading}
                        />
                      </div>
                      <div className="form-group" style={{ margin: 0 }}>
                        <label style={{ fontSize: '0.7rem' }}>У/100</label>
                        <input
                          type="number"
                          value={numericField(ingredient.ingredient_carbohydrates_per_100)}
                          onChange={(e) =>
                            handleIngredientChange(
                              index,
                              'ingredient_carbohydrates_per_100',
                              e.target.value !== '' ? Number(e.target.value) : null
                            )
                          }
                          placeholder="0"
                          min={0}
                          step="0.1"
                          disabled={isLoading}
                        />
                      </div>
                      <div className="form-group" style={{ margin: 0 }}>
                        <label style={{ fontSize: '0.7rem' }}>Вес шт. (г)</label>
                        <input
                          type="number"
                          value={numericField(ingredient.ingredient_piece_weight_g)}
                          onChange={(e) =>
                            handleIngredientChange(
                              index,
                              'ingredient_piece_weight_g',
                              e.target.value !== '' ? Number(e.target.value) : null
                            )
                          }
                          placeholder="60"
                          min={0}
                          step="1"
                          disabled={isLoading}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
          <button
            type="button"
            onClick={handleAddIngredient}
            disabled={isLoading}
            className="btn-secondary"
            style={{ marginTop: '0.75rem' }}
          >
            {t('addIngredient')}
          </button>
          <small className="form-hint">{t('ingredientsHint')}</small>
        </div>

        {/* Nutrients Section */}
        <div className="form-group">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.25rem' }}>
            <label style={{ margin: 0 }}>{t('nutrientsLabel')}</label>
            {nutritionPreview && (
              <button
                type="button"
                onClick={handleApplyCalculated}
                disabled={isLoading}
                className="btn-secondary"
                style={{ fontSize: '0.8rem', padding: '0.3rem 0.75rem' }}
                title="Заполнить поля КБЖУ на основе введённых ингредиентов"
              >
                ↓ Рассчитать из ингредиентов
                {nutritionPreview.skipped > 0 && (
                  <span style={{ marginLeft: '4px', opacity: 0.7 }}>
                    ({nutritionPreview.skipped} пропущено)
                  </span>
                )}
              </button>
            )}
          </div>

          {/* Live preview */}
          {nutritionPreview && (
            <div
              style={{
                background: 'var(--surface-secondary, #f7fafc)',
                border: '1px solid var(--border-color, #e2e8f0)',
                borderRadius: '6px',
                padding: '0.5rem 0.75rem',
                marginBottom: '0.5rem',
                fontSize: '0.82rem',
                color: 'var(--text-secondary, #4a5568)',
              }}
            >
              Расчёт из ингредиентов:{' '}
              <strong>{nutritionPreview.calories} ккал</strong>
              {' · '}Б {nutritionPreview.proteins} г
              {' · '}Ж {nutritionPreview.fats} г
              {' · '}У {nutritionPreview.carbohydrates} г
              {nutritionPreview.skipped > 0 && (
                <span style={{ marginLeft: '8px', opacity: 0.7 }}>
                  ({nutritionPreview.skipped} ингр. без КБЖУ или нераспознанная единица)
                </span>
              )}
            </div>
          )}

          <small className="form-hint" style={{ display: 'block', marginBottom: '0.5rem' }}>
            {t('nutrientsHint')}
          </small>
          <div className="recipe-kbzhu-grid">
            <div className="form-group" style={{ margin: 0 }}>
              <label htmlFor="calories" style={{ fontSize: '0.8rem' }}>{t('caloriesLabel')}</label>
              <input
                id="calories"
                type="number"
                value={calories}
                onChange={(e) => setCalories(e.target.value ? Number(e.target.value) : '')}
                placeholder="0"
                disabled={isLoading}
                min={0}
                step="0.1"
              />
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label htmlFor="proteins" style={{ fontSize: '0.8rem' }}>{t('proteinsLabel')}</label>
              <input
                id="proteins"
                type="number"
                value={proteins}
                onChange={(e) => setProteins(e.target.value ? Number(e.target.value) : '')}
                placeholder="0"
                disabled={isLoading}
                min={0}
                step="0.1"
              />
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label htmlFor="fats" style={{ fontSize: '0.8rem' }}>{t('fatsLabel')}</label>
              <input
                id="fats"
                type="number"
                value={fats}
                onChange={(e) => setFats(e.target.value ? Number(e.target.value) : '')}
                placeholder="0"
                disabled={isLoading}
                min={0}
                step="0.1"
              />
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label htmlFor="carbohydrates" style={{ fontSize: '0.8rem' }}>{t('carbohydratesLabel')}</label>
              <input
                id="carbohydrates"
                type="number"
                value={carbohydrates}
                onChange={(e) => setCarbohydrates(e.target.value ? Number(e.target.value) : '')}
                placeholder="0"
                disabled={isLoading}
                min={0}
                step="0.1"
              />
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
          <button type="submit" className="btn-primary" disabled={isLoading} style={{ flex: 1 }}>
            {isLoading
              ? (recipeId ? t('updating') : t('creating'))
              : (recipeId ? t('updateRecipe') : t('createRecipe'))}
          </button>
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              disabled={isLoading}
              className="btn-secondary"
              style={{ flex: 1 }}
            >
              {t('common:cancel')}
            </button>
          )}
        </div>
      </form>
    </div>
  );
}

export default RecipeForm;
